import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { UserMinus, UserPlus } from 'lucide-react'
import type { Role } from '#shared/types'
import { formatDateLabel, todayLocal } from '#shared/dates'
import { api, HttpError, type TeamMember } from '../api/client'
import { useAuth } from '../auth/AuthContext'

/**
 * Who may sign in, and who runs the place.
 *
 * This page is the allowlist. Adding a row here is what lets an address
 * through Google sign-in at all; suspending one ends that person's session on
 * their next click rather than whenever it happens to expire.
 *
 * The buttons below disable themselves for changes the API would refuse, but
 * that is presentation — `worker/routes/users.ts` holds the actual rules, and
 * there are tests for them.
 */
export function Users() {
  const { user: me } = useAuth()
  const today = useMemo(() => todayLocal(), [])

  const [people, setPeople] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [asManager, setAsManager] = useState(false)
  const [adding, setAdding] = useState(false)

  const activeManagers = people.filter((p) => p.role === 'manager' && p.status === 'active').length

  async function load() {
    try {
      setPeople(await api.listUsers())
      setError(null)
    } catch (err) {
      setError(
        err instanceof HttpError && err.status === 403
          ? 'Managing people is available to managers only.'
          : 'Could not load the team.',
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  function fail(err: unknown, fallback: string) {
    setError(err instanceof HttpError ? err.message : fallback)
  }

  async function handleAdd(event: FormEvent) {
    event.preventDefault()
    if (!email.trim()) return

    setAdding(true)
    try {
      await api.inviteUser({
        email: email.trim(),
        name: name.trim() || undefined,
        role: asManager ? 'manager' : 'member',
      })
      setEmail('')
      setName('')
      setAsManager(false)
      setError(null)
      await load()
    } catch (err) {
      fail(err, 'Could not add that person.')
    } finally {
      setAdding(false)
    }
  }

  async function patch(person: TeamMember, change: { role?: Role; status?: 'active' | 'suspended' }) {
    setBusyId(person.id)
    try {
      await api.updateUser(person.id, change)
      setError(null)
      await load()
    } catch (err) {
      fail(err, 'Could not save that change.')
    } finally {
      setBusyId(null)
    }
  }

  async function handleSuspend(person: TeamMember) {
    if (
      !window.confirm(
        `Suspend ${person.name}? They are signed out immediately and cannot sign back in. Time they have already logged is kept.`,
      )
    ) {
      return
    }
    await patch(person, { status: 'suspended' })
  }

  /** Why a control is unavailable, or null when it is fine to use. */
  function blockedReason(person: TeamMember, change: 'suspend' | 'demote'): string | null {
    const isMe = person.id === me?.id
    const isLastManager =
      person.role === 'manager' && person.status === 'active' && activeManagers <= 1

    if (change === 'suspend' && isMe) return 'You cannot suspend yourself'
    if (isLastManager) return 'There has to be at least one active manager'
    return null
  }

  return (
    <section className="users-page">
      <h1>Users</h1>
      <p className="muted">
        Anyone listed here can sign in with their Google account. Nobody else can.
      </p>

      {error && <p className="error">{error}</p>}

      <div className="card">
        <div className="card-head">
          <h2>Add someone</h2>
        </div>
        <form className="invite-form" onSubmit={handleAdd}>
          <label>
            <span className="field-label">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="someone@soar.earth"
            />
          </label>
          <label>
            <span className="field-label">Name (optional)</span>
            <input
              value={name}
              maxLength={120}
              onChange={(e) => setName(e.target.value)}
              placeholder="From Google if left blank"
            />
          </label>
          <label className="checkline">
            <input
              type="checkbox"
              checked={asManager}
              onChange={(e) => setAsManager(e.target.checked)}
            />
            <span>Manager</span>
          </label>
          <button type="submit" disabled={adding}>
            <UserPlus size={15} aria-hidden="true" />
            {adding ? 'Adding…' : 'Add'}
          </button>
        </form>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>The team</h2>
          <span className="count">
            {people.length} {people.length === 1 ? 'person' : 'people'}
          </span>
        </div>

        {loading && <p className="muted">Loading…</p>}
        {!loading && people.length === 0 && <p className="empty">Nobody yet.</p>}

        {people.length > 0 && (
          <ul className="rows">
            {people.map((person) => {
              const suspended = person.status === 'suspended'
              const cannotSuspend = blockedReason(person, 'suspend')
              const cannotDemote = blockedReason(person, 'demote')

              return (
                <li key={person.id} className={suspended ? 'row is-archived' : 'row'}>
                  <div className="row-body">
                    <span className="row-name">
                      {person.name}
                      {person.id === me?.id && <span className="muted"> · you</span>}
                      {person.role === 'manager' && <span className="badge">Manager</span>}
                      {suspended && <span className="badge badge-off">Suspended</span>}
                    </span>
                    <span className="row-desc">
                      {person.email}
                      {' · '}
                      {person.lastLoginAt
                        ? `last signed in ${formatDateLabel(person.lastLoginAt.slice(0, 10), today)}`
                        : 'never signed in'}
                    </span>
                  </div>

                  <div className="row-actions">
                    <button
                      type="button"
                      className="btn-ghost"
                      disabled={busyId === person.id || Boolean(person.role === 'manager' && cannotDemote)}
                      title={person.role === 'manager' ? (cannotDemote ?? undefined) : undefined}
                      onClick={() =>
                        patch(person, { role: person.role === 'manager' ? 'member' : 'manager' })
                      }
                    >
                      {person.role === 'manager' ? 'Make member' : 'Make manager'}
                    </button>

                    {suspended ? (
                      <button
                        type="button"
                        className="btn-ghost"
                        disabled={busyId === person.id}
                        onClick={() => patch(person, { status: 'active' })}
                      >
                        Restore
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn-ghost btn-danger btn-end"
                        disabled={busyId === person.id || Boolean(cannotSuspend)}
                        title={cannotSuspend ?? undefined}
                        onClick={() => handleSuspend(person)}
                      >
                        <UserMinus size={14} aria-hidden="true" />
                        Suspend
                      </button>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </section>
  )
}
